// @TODO: split into two files, one per component
import { type DailyWeatherPrediction, formatDate, formatNumber, toTimeString } from "@pentacode/core";
import type { ChartDefinition } from "@pentacode/ui/charts";
import { weatherChart, weatherGutter, weatherTooltip } from "@pentacode/ui/charts";
import "@pentacode/ui/components";
import { css, html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { popover } from "../../../directives/popover";
import "../../../elements/popover";
import { shared } from "../../../styles";
import { aggregateHourly } from "../lib/weather-hours";

function currentMinute() {
    const now = new Date();
    now.setSeconds(0, 0);
    return now;
}

@customElement("ptc-weather-detailed")
export class WeatherDetailed extends LitElement {
    @property({ attribute: false })
    data: DailyWeatherPrediction;

    @property()
    city: string;

    @state()
    private _chart?: ChartDefinition;

    @state()
    private _now = currentMinute();

    private _clock?: ReturnType<typeof setInterval>;

    private _chartMarker?: Date;

    private _span = { start: 0, end: 0 };

    connectedCallback() {
        super.connectedCallback();
        this._clock = setInterval(() => (this._now = currentMinute()), 60_000);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        clearInterval(this._clock);
    }

    willUpdate(changes: Map<string, unknown>) {
        if (changes.has("data")) {
            const hourly = this.data?.hourly ?? [];
            this._span = hourly.length
                ? {
                      start: new Date(hourly[0]!.time).getTime(),
                      end: new Date(hourly[hourly.length - 1]!.time).getTime(),
                  }
                : { start: 0, end: 0 };
        }
        const marker = this._marker();
        if (changes.has("data") || marker?.getTime() !== this._chartMarker?.getTime()) {
            this._chartMarker = marker;
            this._chart = this._getChart(marker);
        }
    }

    private _marker() {
        const now = this._now.getTime();
        return now >= this._span.start && now <= this._span.end ? this._now : undefined;
    }

    private _getChart(now?: Date) {
        const hours = (this.data?.hourly ?? []).map((hour) => ({
            time: new Date(hour.time),
            temperature: hour.temperature,
            precipitation: hour.precipitation.amount,
            precipitationProbability: hour.precipitation.propability,
            icon: hour.conditions?.icon,
            conditions: hour.conditions?.description,
        }));
        return hours.length ? weatherChart(hours, now) : undefined;
    }

    private _timeFraction(time: string) {
        const { start, end } = this._span;
        return end > start ? (new Date(time).getTime() - start) / (end - start) : 0;
    }

    static styles = [
        shared,
        css`
            :host {
                display: block;
                position: relative;
            }

            .hourly-strip {
                display: grid;
            }

            .hourly-strip > * {
                grid-area: 1 / 1;
                justify-self: start;
                transform: translateX(-50%);
            }
        `,
    ];

    render() {
        const aggregatedData = aggregateHourly(this.data.hourly);
        return html`
            <div class="horizontally-padded horizontal center-aligning layout">
                <ptc-ui-weather-icon class="gianter" .icon=${this.data.conditions?.icon} animated></ptc-ui-weather-icon>
                <div class="stretch">
                    <div class="bigger">
                        ${this.city}<span class="smaller subtle"> ${formatDate(this.data.date)}</span>
                    </div>
                    <div class="subtle">${this.data.conditions?.description}</div>
                </div>
                <div class="orange colored-text horizontally-padded horizontally-margined">
                    <div class="huger">${this.data.maxTemperature}°</div>
                    <span class="larger subtle">${this.data.minTemperature}°</span>
                </div>
            </div>
            <div class="vertical layout" style="margin-left: 4.8em">
                <div class="center-aligning horizontal layout">
                    <ptc-ui-weather-icon class="larger" icon="sunrise" animated></ptc-ui-weather-icon>
                    <div class="bold right-margined">${toTimeString(new Date(this.data.sunrise))}</div>
                    <ptc-ui-weather-icon class="larger" icon="sunset" animated></ptc-ui-weather-icon>
                    <div class="bold">${toTimeString(new Date(this.data.sunset))}</div>
                </div>
                <div class="center-aligning horizontal layout">
                    <ptc-ui-weather-icon class="larger" icon="clear-day" animated></ptc-ui-weather-icon>
                    <div>
                        <strong>${Math.floor(this.data.hoursOfSunshine / 3600)}</strong> bis
                        <strong>${Math.ceil(this.data.hoursOfSunshine / 3600)}</strong>
                        Sonnenstunden
                    </div>
                </div>
                <div class="center-aligning horizontal layout">
                    <ptc-ui-weather-icon class="larger" icon="raindrops" animated></ptc-ui-weather-icon>
                    <div>
                        <strong>${this.data.precipitation.amount}mm</strong>
                        (${this.data.precipitation.propability}%)
                    </div>
                </div>
                <div class="center-aligning horizontal layout">
                    <ptc-ui-weather-icon class="larger" icon="wind" animated></ptc-ui-weather-icon>
                    <div>bis zu <strong>${this.data.maxWindSpeed} km/h</strong></div>
                </div>
            </div>
            <div class="relative" style="padding: 0 1em 2em 1em;">
                ${this._chart
                    ? html`<ptc-ui-chart
                          label="Wetterverlauf über den Tag: Temperatur, Niederschlagsmenge und Niederschlagswahrscheinlichkeit"
                          .height=${160}
                          .tooltip=${weatherTooltip}
                          .definition=${this._chart}
                      ></ptc-ui-chart>`
                    : nothing}
                <div class="hourly-strip" style="margin: 0 ${weatherGutter.right}px 0 ${weatherGutter.left}px">
                    ${aggregatedData.map(
                        (data) => html`
                            <div
                                class="text-centering center-aligning vertical layout"
                                style="margin-left: ${this._timeFraction(data.time) * 100}%"
                            >
                                <ptc-ui-weather-icon
                                    class="huger"
                                    .icon=${data.conditions?.icon}
                                    animated
                                ></ptc-ui-weather-icon>
                                <div class="orange colored-text">
                                    <strong>${Math.floor(data.temperature)}°C</strong>
                                </div>
                                <div class="smaller blue colored-text">
                                    <strong>${formatNumber(data.precipitation.amount, 1)}mm</strong>
                                </div>
                                <div class="smaller aqua colored-text">
                                    ${Math.ceil(data.precipitation.propability)}%
                                </div>
                            </div>
                        `
                    )}
                </div>
            </div>
        `;
    }
}

@customElement("ptc-weather")
export class Weather extends LitElement {
    @property({ attribute: false })
    data: DailyWeatherPrediction;

    @property()
    city: string;

    @property({ type: Boolean })
    minimal: false;

    static styles = [
        shared,
        css`
            :host {
                display: block;
                position: relative;
            }
        `,
    ];

    render() {
        if (!this.data) {
            return nothing;
        }

        return html`
            <div
                class="horizontal centering layout fullbleed"
                ${popover(html` <ptc-weather-detailed .data=${this.data} .city=${this.city}></ptc-weather-detailed> `, {
                    trigger: "hover",
                    style: "padding: 0;",
                })}
            >
                <ptc-ui-weather-icon
                    class="bigger"
                    .icon=${this.data.conditions?.icon}
                    .label=${this.data.conditions?.description ?? ""}
                ></ptc-ui-weather-icon>
                ${!this.minimal
                    ? html`
                          <div class="orange colored-text left-margined">
                              ${Math.round(this.data.maxTemperature)}°
                              <span class="smaller subtle">${Math.round(this.data.minTemperature)}°</span>
                          </div>
                          <div class="blue colored-text left-margined">${this.data.precipitation.propability}%</div>
                      `
                    : ""}
            </div>
        `;
    }
}
